import React, { forwardRef } from 'react';
import { Box } from '@mui/material';
import type { Icon } from '@tabler/icons-react';
import { IconChevronDown, IconRefresh } from '@tabler/icons-react';

interface CollapsibleSectionProps {
  /** Tabler icon for the section (e.g. IconApps, IconNotes, IconAt). */
  icon: Icon;
  title: string;
  /** Shows a count pill when > 0. */
  count?: number;
  /** When provided, shows a refresh icon (stops propagation so it doesn't toggle). */
  onRefresh?: () => void;
  expanded: boolean;
  onToggle: () => void;
  /** Body padding — pass 0 for content that manages its own (e.g. a flush list). */
  bodyPadding?: string | number;
  children: React.ReactNode;
}

/**
 * Flat collapsible section — header band + expandable body, matching the work-item
 * dashboard customer panel's section-header pattern (replaces card-border accordions).
 * Controlled: the parent owns `expanded`/`onToggle` so it can drive open state
 * (e.g. auto-open on focus). Used by the customer Services/Notes/Aliases panels.
 */
export const CollapsibleSection = forwardRef<HTMLDivElement, CollapsibleSectionProps>(
  ({ icon: IconCmp, title, count, onRefresh, expanded, onToggle, bodyPadding = '10px 16px', children }, ref) => (
    <Box ref={ref} sx={{ minWidth: 0 }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          p: '10px 16px',
          borderTop: '0.5px solid #e2eaf3',
          bgcolor: '#f0f3f7',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: '#e8edf3' },
        }}
      >
        <IconCmp size={18} stroke={1.5} style={{ color: '#aaaaaa', flexShrink: 0 }} />
        <Box
          component="span"
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: '#646464',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            flex: 1,
          }}
        >
          {title}
        </Box>
        {count !== undefined && count > 0 && (
          <Box
            component="span"
            sx={{
              fontSize: '11px',
              fontWeight: 500,
              color: '#4A90D9',
              bgcolor: '#E6F1FB',
              px: '6px',
              py: '1px',
              borderRadius: '8px',
              border: '0.5px solid #B5D4F4',
              flexShrink: 0,
            }}
          >
            {count}
          </Box>
        )}
        {onRefresh && (
          <Box
            component="span"
            title="Refresh"
            onClick={(e) => { e.stopPropagation(); onRefresh(); }}
            sx={{
              display: 'inline-flex',
              flexShrink: 0,
              cursor: 'pointer',
              color: '#cccccc',
              '&:hover': { color: '#888888' },
            }}
          >
            <IconRefresh size={16} stroke={1.5} />
          </Box>
        )}
        <IconChevronDown
          size={16}
          stroke={1.5}
          style={{
            color: '#bbbbbb',
            flexShrink: 0,
            transition: 'transform 0.15s ease',
            transform: expanded ? 'rotate(180deg)' : 'none',
          }}
        />
      </Box>
      {expanded && (
        <Box sx={{ p: bodyPadding, borderTop: '0.5px solid #eef2f7', bgcolor: '#ffffff' }}>
          {children}
        </Box>
      )}
    </Box>
  ),
);
